(() => {
  const SELECTOR = '[data-cwprod-root]';
  const FREE_SHIPPING_GIFT_INDEX = 1;
  const DEFAULT_FREE_SHIPPING_MIN_TOTAL_CENTS = 4000;
  const MAX_TOTAL_QUANTITY = 20;

  class CwProductFunnel {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.state = { pack: 1, freeGifts: 0, purchase: 'otp', quantities: new Map(), busy: false, preferredFrequency: null };
      this.cards = [...root.querySelectorAll('[data-cwprod-pack]')];
      this.variants = [...root.querySelectorAll('[data-cwprod-variant]')];
      this.sellingPlanCache = new WeakMap();
      this.installStepTwoGifts();
      this.populateFrequencyOptions();
      this.bind();
      this.updateExtraStates();
      if ('ResizeObserver' in window) {
        this.giftResizeObserver = new ResizeObserver(() => this.updateGiftProgress());
        this.giftResizeObserver.observe(this.root);
      }
      this.mobileStepQuery = window.matchMedia('(max-width: 749px)');
      this.handleViewportChange = () => this.setMobileStepLock(this.root.classList.contains('cwprod--step-two'));
      this.mobileStepQuery.addEventListener?.('change', this.handleViewportChange);
      window.addEventListener('pageshow', () => {
        this.state.busy = false;
        this.resetExtras();
        this.updateControls();
        this.updateExtraStates();
        this.updatePrices();
        this.updateGiftProgress();
      });
      const selected = this.cards.find(card => card.getAttribute('aria-checked') === 'true') || this.cards[0];
      if (selected) this.selectPack(selected, false);
    }

    installStepTwoGifts() {
      const source = this.root.querySelector('[data-cwprod-step="one"] .cwprod__gifts');
      const stepTwo = this.root.querySelector('[data-cwprod-step="two"]');
      const variants = stepTwo && stepTwo.querySelector('.cwprod__variants-wrap');
      if (!source || !stepTwo || !variants || stepTwo.querySelector('.cwprod__gifts--step-two')) return;
      const clone = source.cloneNode(true);
      clone.classList.add('cwprod__gifts--step-two');
      stepTwo.insertBefore(clone, variants);
    }

    // Every variant carries its own synced Recharge/Shopify selling plan allocations
    // (see [data-cwprod-selling-plans]). Different variants can expose different sets,
    // so the dropdown is built from the union across all of them, not just one group.
    populateFrequencyOptions() {
      const select = this.root.querySelector('[data-cwprod-frequency]');
      if (!select) return;

      const names = [];
      this.variants.forEach(variant => {
        this.sellingPlansForVariant(variant).forEach(plan => {
          if (plan.name && !names.includes(plan.name)) names.push(plan.name);
        });
      });
      if (!names.length) return;

      select.innerHTML = '';
      names.forEach(name => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        select.appendChild(option);
      });
      this.state.preferredFrequency = select.value;
      this.updateSubtitle();
    }

    updateSubtitle() {
      const select = this.root.querySelector('[data-cwprod-frequency]');
      const subtitle = this.root.querySelector('[data-cwprod-subtitle]');
      if (!select || !subtitle) return;

      if (!this.subtitleTemplate) this.subtitleTemplate = subtitle.textContent.trim();

      const optionText = select.options[select.selectedIndex]?.textContent || select.value;
      const days = String(optionText).match(/\d+/)?.[0];

      subtitle.textContent = days && /\d+/.test(this.subtitleTemplate)
        ? this.subtitleTemplate.replace(/\d+/, days)
        : (optionText || this.subtitleTemplate);
    }

    readConfig() {
      try { return JSON.parse(this.root.querySelector('[data-cwprod-config]').textContent); }
      catch (_) { return {}; }
    }

    bind() {
      this.root.addEventListener('click', event => {
        if (this.state.busy) return;
        const pack = event.target.closest('[data-cwprod-pack]');
        const purchase = event.target.closest('[data-cwprod-purchase]');
        const plus = event.target.closest('[data-cwprod-plus]');
        const minus = event.target.closest('[data-cwprod-minus]');
        if (pack) this.selectPack(pack, true);
        else if (purchase) this.selectPurchase(purchase.dataset.cwprodPurchase);
        else if (plus) this.changeVariant(plus.closest('[data-cwprod-variant]'), 1);
        else if (minus) this.changeVariant(minus.closest('[data-cwprod-variant]'), -1);
        else if (event.target.closest('[data-cwprod-next]')) this.showStep('two');
        else if (event.target.closest('[data-cwprod-back]')) this.showStep('one');
        else if (event.target.closest('[data-cwprod-checkout]')) this.addToCart();
      });
      this.root.addEventListener('change', event => {
        if (event.target.matches('[data-cwprod-frequency]')) {
          this.state.preferredFrequency = event.target.value;
          this.updateSubtitle();
          this.updatePrices();
          this.updateGiftProgress();
          return;
        }
        if (!event.target.matches('[data-cwprod-upsell], [data-cwprod-protection]')) return;
        this.updateExtraStates();
        this.updatePrices();
        this.updateGiftProgress();
      });
    }

    selectPack(card, resetVariants) {
      this.cards.forEach(item => item.setAttribute('aria-checked', String(item === card)));
      this.state.pack = Number(card.dataset.quantity || 1);
      this.state.freeGifts = Number(card.dataset.freeGifts || 0);
      if (resetVariants) this.allocateDefaultVariants();
      this.updateGiftProgress();
      this.updatePrices();
      this.updateControls();
      this.root.dispatchEvent(new CustomEvent('cwprod:pack-change', { bubbles: true, detail: { quantity: this.state.pack } }));
    }

    selectPurchase(type) {
      if (type === 'subscribe' && !this.sellingPlanForQuantity(this.state.pack)) return;
      this.state.purchase = type === 'subscribe' ? 'subscribe' : 'otp';
      this.root.querySelectorAll('[data-cwprod-purchase]').forEach(option => option.setAttribute('aria-checked', String(option.dataset.cwprodPurchase === this.state.purchase)));
      const subscribeGroup = this.root.querySelector('[data-cwprod-purchase-group="subscribe"]');
      if (subscribeGroup) subscribeGroup.classList.toggle('is-selected', this.state.purchase === 'subscribe');
      this.updatePrices();
    }

    allocateDefaultVariants() {
      this.state.quantities.clear();
      const preference = ['jet black', 'blush pink', 'ocean blue', 'snow white', 'aloe green', 'sunshine yellow'];
      const available = this.variants
        .filter(item => item.dataset.available === 'true')
        .sort((a, b) => {
          const aTitle = a.querySelector('.cwprod-variant__title').textContent.trim().toLowerCase();
          const bTitle = b.querySelector('.cwprod-variant__title').textContent.trim().toLowerCase();
          const aIndex = preference.findIndex(name => aTitle.includes(name));
          const bIndex = preference.findIndex(name => bTitle.includes(name));
          return (aIndex < 0 ? 99 : aIndex) - (bIndex < 0 ? 99 : bIndex);
        });
      for (let index = 0; index < this.state.pack; index += 1) {
        const variant = available[index % Math.max(available.length, 1)];
        if (!variant) break;
        const id = variant.dataset.variantId;
        this.state.quantities.set(id, (this.state.quantities.get(id) || 0) + 1);
      }
      this.renderVariantCounts();
      this.updateControls();
      this.updateGiftProgress();
    }

    changeVariant(variant, change) {
      if (!variant || variant.dataset.available !== 'true') return;
      const id = variant.dataset.variantId;
      const current = this.state.quantities.get(id) || 0;
      if (change > 0 && this.totalSelected() >= MAX_TOTAL_QUANTITY) return;
      const next = Math.max(0, current + change);
      if (next) this.state.quantities.set(id, next); else this.state.quantities.delete(id);
      this.renderVariantCounts();
      this.updateControls();
      this.updateGiftProgress();
      this.updatePrices();
    }

    totalSelected() {
      return [...this.state.quantities.values()].reduce((sum, quantity) => sum + quantity, 0);
    }

    renderVariantCounts() {
      this.variants.forEach(variant => {
        const count = this.state.quantities.get(variant.dataset.variantId) || 0;
        variant.querySelector('[data-cwprod-count]').textContent = count;
        variant.classList.toggle('is-selected', count > 0);
      });
    }

    updateControls() {
      const total = this.totalSelected();
      const status = this.root.querySelector('[data-cwprod-status]');
      const checkout = this.root.querySelector('[data-cwprod-checkout]');
      if (status && !this.state.busy) status.textContent = '';
      if (checkout) checkout.disabled = total < 1 || this.state.busy;
      this.root.classList.toggle('cwprod--busy', this.state.busy);
      this.variants.forEach(variant => {
        const plus = variant.querySelector('[data-cwprod-plus]');
        const minus = variant.querySelector('[data-cwprod-minus]');
        const count = this.state.quantities.get(variant.dataset.variantId) || 0;
        if (plus) plus.disabled = variant.dataset.available !== 'true' || total >= MAX_TOTAL_QUANTITY;
        if (minus) minus.disabled = variant.dataset.available !== 'true' || count === 0;
      });
    }

    currentProductTotal() {
      const stepTwo = this.root.classList.contains('cwprod--step-two');
      const quantity = stepTwo ? this.totalSelected() : this.state.pack;
      const card = stepTwo ? this.priceCardForQuantity(quantity) : this.cards.find(item => item.getAttribute('aria-checked') === 'true');
      if (!card) return 0;
      const productTotal = Number(card.dataset.eachPrice || 0) * quantity;
      const extrasTotal = stepTwo
        ? [...this.root.querySelectorAll('[data-cwprod-upsell]:checked, [data-cwprod-protection]:checked')]
          .reduce((sum, input) => sum + Number(input.dataset.price || 0), 0)
        : 0;
      return productTotal + extrasTotal;
    }

    updateGiftProgress() {
      const selectedQuantity = this.totalSelected();
      let unlocked = Math.min(3, this.state.freeGifts);
      if (this.root.classList.contains('cwprod--step-two')) {
        unlocked = this.freeGiftsForQuantity(selectedQuantity);
      }
      const freeShippingThreshold = Number(this.config.freeShippingThreshold) || DEFAULT_FREE_SHIPPING_MIN_TOTAL_CENTS;
      const meetsFreeShippingThreshold = this.currentProductTotal() >= freeShippingThreshold;
      this.root.querySelectorAll('[data-cwprod-gift]').forEach(gift => {
        const index = Number(gift.dataset.index || 0);
        const isUnlocked = index < unlocked || (index === FREE_SHIPPING_GIFT_INDEX && meetsFreeShippingThreshold);
        gift.classList.toggle('is-unlocked', isUnlocked);
      });
      const progressUnlocked = meetsFreeShippingThreshold ? Math.max(unlocked, FREE_SHIPPING_GIFT_INDEX + 1) : unlocked;
      const progressRatio = progressUnlocked <= 1 ? 0 : (progressUnlocked - 1) / 2;
      this.root.querySelectorAll('[data-cwprod-gift-progress]').forEach(progress => {
        const track = progress.parentElement;
        const circle = track && track.querySelector('.cwprod-gift__media');
        const trackWidth = track ? track.getBoundingClientRect().width : 0;
        const circleWidth = circle ? circle.getBoundingClientRect().width : 0;
        const progressWidth = Math.max(0, (trackWidth - circleWidth) * progressRatio);
        progress.textContent = '\u200B';
        progress.style.display = 'block';
        progress.style.width = `${progressWidth}px`;
        progress.style.height = '5px';
        progress.style.background = 'var(--Yellow, #D6A800)';
        progress.style.setProperty('--cwprod-gift-progress', progressRatio);
        progress.dataset.cwprodProgressState = String(unlocked);
      });
    }

    updatePrices() {
      this.updatePackPrices();
      const stepTwo = this.root.classList.contains('cwprod--step-two');
      const quantity = stepTwo ? this.totalSelected() : this.state.pack;
      const card = stepTwo ? this.priceCardForQuantity(quantity) : this.cards.find(item => item.getAttribute('aria-checked') === 'true');
      if (!card) return;
      const productTotal = Number(card.dataset.eachPrice || 0) * quantity;
      const sellingPlan = this.sellingPlanForQuantity(quantity);
      const subscriptionOverride = this.subscriptionOverrideForQuantity(quantity);
      const subscriptionEachPrice = this.subscriptionEachPriceFor(card, sellingPlan, subscriptionOverride);
      const subscriptionProductTotal = sellingPlan ? subscriptionEachPrice * quantity : productTotal;
      const subscriptionCompareTotal = sellingPlan ? Number(sellingPlan.compareAtPrice || 0) * quantity : productTotal;
      const extrasTotal = [...this.root.querySelectorAll('[data-cwprod-upsell]:checked, [data-cwprod-protection]:checked')]
        .reduce((sum, input) => sum + Number(input.dataset.price || 0), 0);
      const checkoutProductTotal = this.state.purchase === 'subscribe' && sellingPlan ? subscriptionProductTotal : productTotal;
      const total = checkoutProductTotal + (stepTwo ? extrasTotal : 0);
      const compareTotal = Number(card.dataset.compareEachPrice || card.dataset.eachPrice || 0) * quantity;
      const otp = this.root.querySelector('[data-cwprod-otp-price]');
      const sub = this.root.querySelector('[data-cwprod-sub-price]');
      const otpCompare = this.root.querySelector('[data-cwprod-otp-compare]');
      const subCompare = this.root.querySelector('[data-cwprod-sub-compare]');
      if (otp) otp.textContent = this.formatMoney(productTotal);
      if (sub) sub.textContent = this.formatMoney(subscriptionProductTotal);
      if (otpCompare) otpCompare.textContent = this.formatMoney(compareTotal);
      if (subCompare) subCompare.textContent = this.formatMoney(subscriptionCompareTotal || productTotal);
      const subBadge = this.root.querySelector('[data-cwprod-sub-badge]');
      if (subBadge && sellingPlan) {
        const comparison = Number(sellingPlan.compareAtPrice || 0);
        const planPrice = subscriptionEachPrice;
        const badgeSource = subscriptionOverride?.badge ?? subscriptionOverride?.discount;
        const discount = Math.round(Number(badgeSource || (comparison > planPrice ? (1 - planPrice / comparison) * 100 : 0)));
        subBadge.textContent = discount > 0 ? `${discount}% OFF` : '';
        subBadge.hidden = discount <= 0;
      }
      const checkoutTotal = this.root.querySelector('[data-cwprod-checkout-total]');
      if (checkoutTotal) checkoutTotal.textContent = this.formatMoney(total);
      this.updateSavingsBadge();
    }

    updateSavingsBadge() {
      const badge = this.root.querySelector('[data-cwprod-savings-badge]');
      if (!badge) return;
      const total = this.totalSelected();
      const nextCard = this.cards
        .slice()
        .sort((a, b) => Number(a.dataset.quantity) - Number(b.dataset.quantity))
        .find(card => Number(card.dataset.quantity) > total && card.querySelector('.cwprod-pack__discount:not([hidden])'));
      if (!nextCard) {
        badge.textContent = this.config.maxSavingsBadge || 'MAX SAVINGS UNLOCKED';
        return;
      }
      const nextQuantity = Number(nextCard.dataset.quantity);
      const discountText = nextCard.querySelector('.cwprod-pack__discount')?.textContent || '';
      const discount = discountText.match(/\d+(?:[.,]\d+)?/)?.[0] || '0';
      const template = this.config.savingsBadgeTemplate || 'ADD X More & Save up XX% Off';
      badge.textContent = template.replace(/XX/g, discount).replace(/X/g, String(nextQuantity - total));
    }

    updatePackPrices() {
      this.cards.forEach(card => {
        const quantity = Number(card.dataset.quantity || 1);
        const plan = this.state.purchase === 'subscribe' ? this.sellingPlanForQuantity(quantity) : null;
        const subscriptionOverride = plan ? this.subscriptionOverrideForQuantity(quantity) : null;
        const eachPrice = plan ? this.subscriptionEachPriceFor(card, plan, subscriptionOverride) : Number(card.dataset.eachPrice || 0);
        const compareEachPrice = plan
          ? Number(plan.compareAtPrice || card.dataset.compareEachPrice || eachPrice)
          : Number(card.dataset.compareEachPrice || eachPrice);
        const price = card.querySelector('.cwprod-pack__price');
        if (price) price.textContent = this.formatMoney(eachPrice);

        let compare = card.querySelector('.cwprod-pack__compare');
        if (!compare && compareEachPrice > eachPrice) {
          compare = document.createElement('span');
          compare.className = 'cwprod-pack__compare';
          compare.dataset.cwprodGeneratedCompare = 'true';
          card.querySelector('.cwprod-pack__top')?.appendChild(compare);
        }
        if (compare) {
          compare.textContent = this.formatMoney(compareEachPrice);
          compare.hidden = compareEachPrice <= eachPrice;
        }
        let discount = card.querySelector('.cwprod-pack__discount');
        const fullPrice = card.querySelector('.cwprod-pack__full');
        if (!discount && plan && subscriptionOverride?.discount && quantity !== 1) {
          discount = document.createElement('span');
          discount.className = 'cwprod-pack__discount';
          discount.dataset.cwprodGeneratedDiscount = 'true';
          card.querySelector('.cwprod-pack__bottom')?.prepend(discount);
        }
        if (discount) {
          if (!discount.dataset.cwprodOtpDiscount) discount.dataset.cwprodOtpDiscount = discount.textContent.trim();
          discount.textContent = plan && subscriptionOverride?.discount
            ? `${Math.round(Number(subscriptionOverride.badge ?? subscriptionOverride.discount))}% OFF`
            : discount.dataset.cwprodOtpDiscount;
          discount.hidden = Boolean((quantity === 1 && plan) || (discount.dataset.cwprodGeneratedDiscount && !plan));
        }
        if (fullPrice) fullPrice.hidden = false;
      });
    }

    tierForQuantity(quantity) {
      return quantity >= 6 ? 6 : quantity >= 3 ? 3 : Math.max(1, quantity);
    }

    subscriptionOverrideForQuantity(quantity) {
      const tier = this.tierForQuantity(quantity);
      return this.config.subscriptionOverrides?.[String(tier)] || null;
    }

    // The subscription price is derived from the same base (1-towel) price the OTP
    // packs use — which is already market/currency-correct straight from Liquid — by
    // applying the configured discount percentage for that tier, instead of trusting
    // a flat editor-entered price that would need its own currency conversion.
    subscriptionEachPriceFor(card, sellingPlan, subscriptionOverride) {
      const basePrice = Number(card?.dataset.basePrice || card?.dataset.eachPrice || 0);
      const discount = Number(subscriptionOverride?.discount || 0);
      if (discount > 0) return Math.round(basePrice * (1 - discount / 100));
      return Number(sellingPlan?.price || card?.dataset.eachPrice || 0);
    }

    // Checkboxes keep their checked state across back/forward navigation (bfcache),
    // but our own UI (the +/✓ icon, prices, gift progress) doesn't re-run to match —
    // so returning to the page after checkout could show a stale, inconsistent state.
    // Resetting both back to their original markup default on pageshow keeps things
    // predictable instead of trying to resync to whatever the browser restored.
    resetExtras() {
      this.root.querySelectorAll('[data-cwprod-upsell], [data-cwprod-protection]').forEach(input => {
        input.checked = input.defaultChecked;
      });
    }

    updateExtraStates() {
      const upsell = this.root.querySelector('[data-cwprod-upsell]');
      const plus = this.root.querySelector('.cwprod-extra__plus');
      if (plus) plus.textContent = upsell && upsell.checked ? '✓' : '+';
    }

    sellingPlansForVariant(variant) {
      if (!variant) return [];
      if (this.sellingPlanCache.has(variant)) return this.sellingPlanCache.get(variant);
      let plans = [];
      try { plans = JSON.parse(variant.querySelector('[data-cwprod-selling-plans]')?.textContent || '[]'); }
      catch (_) { plans = []; }
      this.sellingPlanCache.set(variant, plans);
      return plans;
    }

    planQuantity(name) {
      const normalized = String(name || '').toLowerCase();
      const number = normalized.match(/\b([1-6])\b/);
      if (number) return Number(number[1]);
      if (/\b(single|one)\b/.test(normalized)) return 1;
      if (/\b(duo|two)\b/.test(normalized)) return 2;
      if (/\b(trio|three)\b/.test(normalized)) return 3;
      if (/\b(family|six)\b/.test(normalized)) return 6;
      return 0;
    }

    sellingPlanForVariant(variant, quantity) {
      const plans = this.sellingPlansForVariant(variant);
      if (!plans.length) return null;
      const tier = this.tierForQuantity(quantity);
      let candidates = plans.filter(plan => this.planQuantity(plan.groupName || plan.name) === tier);
      if (!candidates.length) candidates = plans.filter(plan => this.planQuantity(plan.groupName || plan.name) === 0);
      if (!candidates.length) candidates = plans;
      const preferred = this.state.preferredFrequency || this.config.preferredFrequency;
      const exactMatch = preferred && candidates.find(plan => plan.name === preferred);
      if (exactMatch) return exactMatch;
      const frequency = String(preferred || '').match(/\d+/)?.[0];
      return (frequency && candidates.find(plan => `${plan.name} ${plan.groupName || ''}`.includes(frequency))) || candidates[0];
    }

    sellingPlanForQuantity(quantity) {
      const variant = this.variants.find(item => item.dataset.available === 'true' && this.sellingPlansForVariant(item).length);
      return this.sellingPlanForVariant(variant, quantity);
    }

    priceCardForQuantity(quantity) {
      const tier = this.tierForQuantity(quantity);
      return this.cards.find(item => Number(item.dataset.quantity || 1) === tier) || this.cards[0];
    }

    freeGiftsForQuantity(quantity) {
      return quantity >= 6 ? 3 : quantity >= 3 ? 2 : quantity >= 2 ? 1 : 0;
    }

    currentFreeGifts() {
      return this.root.classList.contains('cwprod--step-two')
        ? this.freeGiftsForQuantity(this.totalSelected())
        : Math.min(3, this.state.freeGifts);
    }

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      const format = this.config.moneyFormat || '${{amount}}';
      const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);

      const withDelimiters = (precision, thousands, decimal) => {
        const negative = value < 0 ? '-' : '';
        const parts = Math.abs(value).toFixed(precision).split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        return negative + dollars + (parts[1] ? `${decimal}${parts[1]}` : '');
      };

      let amount;
      switch (placeholderMatch && placeholderMatch[1]) {
        case 'amount_no_decimals':
          amount = withDelimiters(0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          amount = withDelimiters(2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          amount = withDelimiters(0, '.', ',');
          break;
        case 'amount_with_space_separator':
          amount = withDelimiters(2, ' ', ',');
          break;
        case 'amount_with_apostrophe_separator':
          amount = withDelimiters(2, "'", '.');
          break;
        default:
          amount = withDelimiters(2, ',', '.');
      }

      const rendered = placeholderMatch ? format.replace(placeholderMatch[0], amount) : format;
      const parser = document.createElement('span');
      parser.innerHTML = rendered;
      return parser.textContent.trim();
    }

    showStep(step) {
      if (step === 'two' && this.totalSelected() !== this.state.pack) this.allocateDefaultVariants();
      this.root.querySelector('[data-cwprod-step="one"]').hidden = step !== 'one';
      this.root.querySelector('[data-cwprod-step="two"]').hidden = step !== 'two';
      this.root.classList.toggle('cwprod--step-two', step === 'two');
      this.setMobileStepLock(step === 'two');
      const info = this.root.closest('.product__info-container');
      if (info) info.classList.toggle('cwprod-step-two-active', step === 'two');
      this.updateGiftProgress();
      this.updatePrices();
      this.updateControls();
      if (step === 'one' || !this.mobileStepQuery.matches) this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setMobileStepLock(open) {
      const locked = Boolean(open && this.mobileStepQuery.matches);
      document.documentElement.classList.toggle('cwprod-mobile-step-open', locked);
      document.body.classList.toggle('cwprod-mobile-step-open', locked);
    }

    buildItems() {
      const items = [];
      const selectedQuantity = this.totalSelected();
      this.variants.forEach(variant => {
        const quantity = this.state.quantities.get(variant.dataset.variantId) || 0;
        if (!quantity) return;
        const item = { id: Number(variant.dataset.variantId), quantity };
        if (this.state.purchase === 'subscribe') {
          const sellingPlan = this.sellingPlanForVariant(variant, selectedQuantity);
          if (sellingPlan) item.selling_plan = Number(sellingPlan.id);
        }
        items.push(item);
      });
      const freeGifts = this.currentFreeGifts();
      if (freeGifts >= 1 && Number(this.config.firstGiftId)) items.push({ id: Number(this.config.firstGiftId), quantity: 1, properties: { _cw_free_gift: 'true' } });
      if (freeGifts >= 3 && Number(this.config.secondGiftId)) items.push({ id: Number(this.config.secondGiftId), quantity: 1, properties: { _cw_free_gift: 'true' } });
      const upsell = this.root.querySelector('[data-cwprod-upsell]:checked');
      const protection = this.root.querySelector('[data-cwprod-protection]:checked');
      if (upsell) items.push({ id: Number(upsell.value), quantity: 1 });
      if (protection) items.push({ id: Number(protection.value), quantity: 1 });
      return items;
    }

    async clearCart() {
      const clearUrl = this.config.cartClearUrl || '/cart/clear.js';
      for (let attempt = 0; attempt < 3; attempt += 1) {
        await fetch(clearUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, cache: 'no-store'
        });
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const cart = await response.json();
        if (!cart.item_count) return;
        await new Promise(resolve => setTimeout(resolve, 150));
      }
    }

    async addToCart() {
      if (this.state.busy || this.totalSelected() < 1) return;
      this.state.busy = true;
      this.updateControls();
      const status = this.root.querySelector('[data-cwprod-status]');
      if (status) status.textContent = 'Processing your order…';
      try {
        await this.clearCart();
        const response = await fetch(this.config.cartAddUrl || '/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: this.buildItems() })
        });
        if (!response.ok) throw new Error((await response.json()).description || 'Unable to add this bundle.');
        window.location.assign(this.config.checkoutUrl || '/checkout');
      } catch (error) {
        if (status) status.textContent = error.message;
        this.state.busy = false;
        this.updateControls();
      }
    }
  }

  const mount = scope => scope.querySelectorAll(SELECTOR).forEach(root => { if (!root.cwProductFunnel) root.cwProductFunnel = new CwProductFunnel(root); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document)); else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
})();
